/**
 * Planning settings control: cog icon opens a floating panel (Discuss/Bake, stage, undo, history, participants).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronUpIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import {
  invitePlanningParticipant,
  listPlanningParticipants,
  listPlanningVersions,
  removePlanningParticipant,
  restorePlanningVersion,
  setPlanningMode,
  setPlanningStage,
  undoPlanningDraft,
  type DraftSnapshot,
} from '../../services/planningService';
import { templateService } from '../../services/templateService';

type Mode = 'discuss' | 'bake';
type Stage = 'vision' | 'objectives' | 'strategies' | 'projects';
type SessionRole = 'viewer' | 'discuss' | 'plan' | 'admin';

const STAGES: Stage[] = ['vision', 'objectives', 'strategies', 'projects'];

export interface PlanningCollabToolbarProps {
  sessionId: string;
  mode: Mode;
  currentStage: Stage;
  draftVersion: number;
  layerState: Record<string, { locked?: boolean; stale?: boolean }> | null;
  myRole: string | null;
  /** When false, hide People/invite (public & anonymous solo sessions). */
  canCollaborate?: boolean;
  onModeChange: (mode: Mode) => void;
  onStageChange: (stage: Stage) => void;
  onLayerStateChange?: (layerState: Record<string, { locked?: boolean; stale?: boolean }>) => void;
  onDraftChange: (draft: DraftSnapshot | null, version: number) => void;
  /** Reload chat from server after undo/restore so system events appear. */
  onReloadMessages?: () => void;
}

export const PlanningCollabToolbar: React.FC<PlanningCollabToolbarProps> = ({
  sessionId,
  mode,
  currentStage,
  draftVersion,
  layerState,
  myRole,
  canCollaborate = false,
  onModeChange,
  onStageChange,
  onLayerStateChange,
  onDraftChange,
  onReloadMessages,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const tenantId = user?.tenantId ?? null;
  const canBake = !canCollaborate || myRole === 'plan' || myRole === 'admin' || myRole == null;
  const isAdmin = canCollaborate && myRole === 'admin';
  const [expanded, setExpanded] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [versions, setVersions] = useState<
    Array<{ versionNumber: number; layer: string; label: string | null; createdAt: string }>
  >([]);
  const [participants, setParticipants] = useState<
    Array<{ userId: string; role: string; email?: string | null; firstName?: string | null; lastName?: string | null }>
  >([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState<
    Array<{ id: string; email?: string; firstName?: string; lastName?: string; name?: string }>
  >([]);
  const [inviteRole, setInviteRole] = useState<SessionRole>('discuss');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<number | null>(null);

  const scheduleCollapse = useCallback(() => {
    if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = window.setTimeout(() => {
      setExpanded(false);
      setVersionsOpen(false);
      setPeopleOpen(false);
    }, 900);
  }, []);

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const onDoc = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpanded(false);
        setVersionsOpen(false);
        setPeopleOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [expanded]);

  const refreshVersions = useCallback(async () => {
    const res = await listPlanningVersions(sessionId);
    if (res.success && res.versions) setVersions(res.versions);
  }, [sessionId]);

  const refreshPeople = useCallback(async () => {
    const res = await listPlanningParticipants(sessionId);
    if (res.success && res.participants) setParticipants(res.participants);
  }, [sessionId]);

  useEffect(() => {
    if (versionsOpen) void refreshVersions();
  }, [versionsOpen, refreshVersions]);

  useEffect(() => {
    if (peopleOpen) void refreshPeople();
  }, [peopleOpen, refreshPeople]);

  useEffect(() => {
    if (!peopleOpen || searchQ.trim().length < 2) {
      setSearchHits([]);
      return;
    }
    // Only search users who belong to the current tenant (never the global directory)
    if (!tenantId) {
      setSearchHits([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const hits = await templateService.searchUsers(searchQ.trim(), tenantId);
          setSearchHits(
            (hits || [])
              .map((u: Record<string, unknown>) => ({
                id: String(u['id'] ?? u['userId'] ?? ''),
                email: u['email'] != null ? String(u['email']) : undefined,
                firstName:
                  u['firstName'] != null
                    ? String(u['firstName'])
                    : u['first_name'] != null
                      ? String(u['first_name'])
                      : undefined,
                lastName:
                  u['lastName'] != null
                    ? String(u['lastName'])
                    : u['last_name'] != null
                      ? String(u['last_name'])
                      : undefined,
                name: u['name'] != null ? String(u['name']) : undefined,
              }))
              .filter((u) => u.id)
          );
        } catch {
          setSearchHits([]);
        }
      })();
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchQ, peopleOpen, tenantId]);

  const handleMode = async (next: Mode) => {
    if (!canBake && next === 'bake') return;
    setBusy(true);
    setErr(null);
    try {
      const res = await setPlanningMode(sessionId, next);
      if (res.success) {
        onModeChange(next);
        scheduleCollapse();
      } else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    if (!canBake) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await undoPlanningDraft(sessionId);
      if (res.success) {
        onDraftChange(res.draft ?? null, res.draftVersion ?? draftVersion);
        onReloadMessages?.();
        if (versionsOpen) void refreshVersions();
        scheduleCollapse();
      } else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (n: number) => {
    if (!canBake) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await restorePlanningVersion(sessionId, n);
      if (res.success) {
        onDraftChange(res.draft ?? null, res.draftVersion ?? draftVersion);
        onReloadMessages?.();
        void refreshVersions();
        scheduleCollapse();
      } else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleStageClick = async (stage: Stage) => {
    if (!canBake) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await setPlanningStage(sessionId, stage);
      if (res.success) {
        onStageChange(stage);
        if (res.layerState) onLayerStateChange?.(res.layerState);
        scheduleCollapse();
      } else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (userId: string) => {
    if (!isAdmin) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await invitePlanningParticipant(sessionId, userId, inviteRole);
      if (res.success) {
        setSearchQ('');
        setSearchHits([]);
        void refreshPeople();
      } else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!isAdmin) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await removePlanningParticipant(sessionId, userId);
      if (res.success) void refreshPeople();
      else setErr(res.message || t('planning.collab.error'));
    } finally {
      setBusy(false);
    }
  };

  const stageLabel = (s: Stage) => t(`planning.collab.stage.${s}`);
  const modeLabel = mode === 'bake' ? t('planning.collab.bake') : t('planning.collab.discuss');
  const summary = `${modeLabel} · ${stageLabel(currentStage)} · v${draftVersion}`;
  const settingsTitle = `${t('planning.collab.settings')} — ${summary}`;

  return (
    <div ref={panelRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] shadow-sm hover:bg-[var(--color-surface-hover)]"
        title={settingsTitle}
        aria-label={settingsTitle}
        aria-expanded={expanded}
        aria-haspopup="dialog"
      >
        <Cog6ToothIcon className="h-4 w-4 shrink-0" aria-hidden />
      </button>

      {expanded && (
        <div
          className="absolute right-0 top-full z-50 mt-1.5 flex w-[min(22rem,calc(100vw-3rem))] max-h-[min(70vh,28rem)] flex-col gap-2 overflow-y-auto overscroll-y-contain rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 shadow-xl"
          role="dialog"
          aria-label={t('planning.collab.panelTitle')}
        >
          <div className="flex items-center justify-between gap-2 sticky top-0 bg-[var(--color-surface-bg)] pb-1">
            <span className="text-xs font-semibold text-[var(--color-surface-text)] truncate">
              {t('planning.collab.panelTitle')}
            </span>
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                setVersionsOpen(false);
                setPeopleOpen(false);
              }}
              className="shrink-0 p-0.5 rounded text-[var(--color-surface-text-secondary)] hover:text-[var(--color-surface-text)]"
              title={t('planning.collab.collapsePanel')}
              aria-label={t('planning.collab.collapsePanel')}
            >
              <ChevronUpIcon className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <p className="text-[11px] text-[var(--color-surface-text-secondary)] -mt-1 truncate" title={summary}>
            {summary}
          </p>

          <div className="flex flex-col gap-2.5">
            {/* Line 1 — Mode */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
                {t('planning.collab.mode')}
              </span>
              <div className="inline-flex w-fit rounded border border-[var(--color-border)] overflow-hidden text-xs">
                <button
                  type="button"
                  disabled={busy}
                  className={`px-2.5 py-1 ${mode === 'discuss' ? 'bg-[var(--color-primary)] text-[var(--color-primary-text)]' : 'bg-[var(--color-surface)] text-[var(--color-text-primary)]'}`}
                  onClick={() => void handleMode('discuss')}
                >
                  {t('planning.collab.discuss')}
                </button>
                <button
                  type="button"
                  disabled={busy || !canBake}
                  className={`px-2.5 py-1 ${mode === 'bake' ? 'bg-[var(--color-primary)] text-[var(--color-primary-text)]' : 'bg-[var(--color-surface)] text-[var(--color-text-primary)]'}`}
                  onClick={() => void handleMode('bake')}
                  title={!canBake ? t('planning.collab.bakeRequiresPlan') : undefined}
                >
                  {t('planning.collab.bake')}
                </button>
              </div>
            </div>

            {/* Line 2 — Version */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
                {t('planning.collab.version')}
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs tabular-nums text-[var(--color-surface-text)]">
                  v{draftVersion}
                </span>
                <button
                  type="button"
                  disabled={busy || !canBake || draftVersion < 1}
                  className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-primary)] disabled:opacity-50"
                  onClick={() => void handleUndo()}
                >
                  {t('planning.collab.undo')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-primary)]"
                  onClick={() => setVersionsOpen((o) => !o)}
                >
                  {t('planning.collab.history')}
                </button>
              </div>
              {versionsOpen && (
                <ul className="max-h-32 overflow-y-auto text-xs space-y-1 border border-[var(--color-border)] rounded p-2 bg-[var(--color-background)]">
                  {versions.length === 0 && (
                    <li className="text-[var(--color-text-secondary)]">{t('planning.collab.noVersions')}</li>
                  )}
                  {versions.map((v) => (
                    <li key={v.versionNumber} className="flex items-center justify-between gap-2">
                      <span>
                        v{v.versionNumber} · {v.layer}
                        {v.label ? ` · ${v.label}` : ''}
                      </span>
                      {canBake && (
                        <button
                          type="button"
                          className="underline text-[var(--color-primary)]"
                          disabled={busy}
                          onClick={() => void handleRestore(v.versionNumber)}
                        >
                          {t('planning.collab.restore')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Line 3 — Steps */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
                {t('planning.collab.steps')}
              </span>
              <div className="flex flex-wrap gap-1">
                {STAGES.map((s) => {
                  const flags = layerState?.[s];
                  const active = currentStage === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={busy}
                      onClick={() => void handleStageClick(s)}
                      className={`text-xs px-2 py-0.5 rounded border ${
                        active
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                          : 'border-[var(--color-border)] text-[var(--color-text-secondary)]'
                      }`}
                      title={
                        flags?.stale
                          ? t('planning.collab.stale')
                          : flags?.locked
                            ? t('planning.collab.locked')
                            : t('planning.collab.unlocked')
                      }
                    >
                      {stageLabel(s)}
                      {flags?.locked ? ' ✓' : ''}
                      {flags?.stale ? ' !' : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Line 4 — Manage Team (tenant collab only) */}
            {canCollaborate && (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-surface-text-secondary)]">
                  {t('planning.collab.manageTeam')}
                </span>
                {isAdmin ? (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      className="w-fit text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-primary)]"
                      onClick={() => setPeopleOpen((o) => !o)}
                    >
                      {peopleOpen ? t('planning.collab.hideTeam') : t('planning.collab.people')}
                    </button>
                    {peopleOpen && (
                      <div className="text-xs border border-[var(--color-border)] rounded p-2 bg-[var(--color-background)] space-y-2">
                        <div className="flex flex-wrap gap-2 items-center">
                          <input
                            type="search"
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            placeholder={t('planning.collab.searchUsers')}
                            className="flex-1 min-w-[8rem] px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-primary)]"
                          />
                          <select
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value as SessionRole)}
                            className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)]"
                          >
                            <option value="viewer">{t('planning.collab.role.viewer')}</option>
                            <option value="discuss">{t('planning.collab.role.discuss')}</option>
                            <option value="plan">{t('planning.collab.role.plan')}</option>
                            <option value="admin">{t('planning.collab.role.admin')}</option>
                          </select>
                        </div>
                        {searchHits.length > 0 && (
                          <ul className="max-h-24 overflow-y-auto space-y-1">
                            {searchHits.map((u) => (
                              <li key={u.id} className="flex justify-between gap-2">
                                <span className="truncate">
                                  {[u.firstName, u.lastName].filter(Boolean).join(' ') ||
                                    u.name ||
                                    u.email ||
                                    u.id}
                                </span>
                                <button
                                  type="button"
                                  className="underline text-[var(--color-primary)] shrink-0"
                                  disabled={busy}
                                  onClick={() => void handleInvite(u.id)}
                                >
                                  {t('planning.collab.invite')}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <ul className="space-y-1 border-t border-[var(--color-border)] pt-2">
                          {participants.map((p) => (
                            <li key={p.userId} className="flex justify-between gap-2">
                              <span className="truncate">
                                {[p.firstName, p.lastName].filter(Boolean).join(' ') ||
                                  p.email ||
                                  p.userId}
                                <span className="text-[var(--color-text-secondary)]"> · {p.role}</span>
                              </span>
                              <button
                                type="button"
                                className="underline text-[var(--color-error)] shrink-0"
                                disabled={busy}
                                onClick={() => void handleRemove(p.userId)}
                              >
                                {t('planning.collab.remove')}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-[var(--color-surface-text-secondary)]">
                    {t('planning.collab.manageTeamAdminOnly')}
                  </p>
                )}
              </div>
            )}
          </div>

          {err && <p className="text-xs text-[var(--color-error)]">{err}</p>}
        </div>
      )}
    </div>
  );
};
